import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';

import { SbFieldComponent } from './sb-field-utils';

/** Type definition for elements that represent a grouped list of items
 *  This components expects the values to be represented as an array
 *  of GroupedItem's.
 *  It will return the selected item as an object with
 *  {
 *    group: "Selected group",
 *    value: "Selected value"
 *  }
 **/
export interface GroupedItem {
    group: string;
    values: string[];
}

/**
 * Filter function that makes a case insensitive filtering.
 * It does a basic string "includes" check of each element in the list.
 * @param opt
 * @param value
 */
export const _filter = (opt: string[], value: string): string[] => {
    if (value && typeof value === 'string') {
        const filterValue = value.toLowerCase();
        return opt.filter((item) => item.toLowerCase().includes(filterValue));
    }
    return opt;
};

@Component({
    selector: 'app-sb-grouped-autocomplete',
    template: `
        <mat-form-field>
            <mat-label>{{ to.label }}</mat-label>
            <input
                type="text"
                [name]="getRandomName()"
                matInput
                autocomplete="do-not-autocomplete"
                [formControl]="formControl"
                [matAutocomplete]="key"
                (focus)="onFocus($event)"
                (blur)="onLeave($event)"
                [required]="to.required"
            />
            <mat-autocomplete #key="matAutocomplete" [displayWith]="displayWithFn">
                <mat-optgroup *ngFor="let group of filteredItems" [label]="group.group">
                    <mat-option
                        *ngFor="let option of group.values"
                        [value]="getValue(option, group.group)"
                    >
                        {{ option }}
                    </mat-option>
                </mat-optgroup>
            </mat-autocomplete>
        </mat-form-field>
    `
})
export class SbGroupedAutoCompleteComponent extends SbFieldComponent implements OnInit {
    constructor() {
        super();
    }

    filteredItems: GroupedItem[] | Observable<GroupedItem[]>;

    ngOnInit() {
        this.filteredItems = this.field.templateOptions.options;

        // Listen to valueChanges in the formControl (the text in the input field) to do custom filtering.
        this.formControl.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
            this.filter(value);
        });
    }

    onFocus(event) {
        this.filter('');
    }

    // When this is dirty an empty string is set in the form control - set the value to null to ensure not passing empty string values to server
    onLeave(event) {
        if (this.formControl.value === '') {
            this.formControl.setValue(null);
        }
    }

    /** Filter out matching elements (values) in each group.
     *  Skips groups that are empty (0 elements)
     **/
    filter(value) {
        const options = this.field.templateOptions.options;
        if (options && Array.isArray(options)) {
            const temp = (options as any[])
                .map((option) => {
                    return { group: option.group, values: _filter(option.values, value) };
                })
                .filter((group) => group.values.length > 0);
            this.filteredItems = temp;
        }
    }

    getValue(value: string, group: string) {
        return { value: value, group: group };
    }

    /** A function that takes selected option together with template options */
    displayWithFn = (argument) => {
        return this.displayFn(argument, this.to);
    };

    /** Display the selected option together with the option group */
    displayFn(option: any, templateOptions): any {
        if (option) {
            return option.value + ' (' + option.group + ')';
        }
        return option;
    }
}
